import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';

type EasingName = 'cubic' | 'expo';

interface UseCountUpOptions {
  duration?: number;
  start?: number;
  enabled?: boolean;
  easing?: EasingName;
}

function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function easeOutExpo(t: number): number {
  return t === 1 ? 1 : 1 - 2 ** (-10 * t);
}

const easingFns: Record<EasingName, (t: number) => number> = {
  cubic: easeOutCubic,
  expo: easeOutExpo,
};

export function useCountUp(
  end: number,
  durationOrOptions: number | UseCountUpOptions = 1000,
  start = 0,
  enabled = true,
): number {
  const opts: Required<UseCountUpOptions> =
    typeof durationOrOptions === 'number'
      ? { duration: durationOrOptions, start, enabled, easing: 'cubic' }
      : {
          duration: durationOrOptions.duration ?? 1000,
          start: durationOrOptions.start ?? start,
          enabled: durationOrOptions.enabled ?? enabled,
          easing: durationOrOptions.easing ?? 'cubic',
        };

  const reduced = useReducedMotion();
  const [current, setCurrent] = useState(opts.start);
  const rafId = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const previousEnd = useRef(opts.start);

  useEffect(() => {
    if (!opts.enabled) {
      setCurrent(opts.start);
      previousEnd.current = opts.start;
      return;
    }

    if (reduced) {
      setCurrent(end);
      previousEnd.current = end;
      return;
    }

    const from = previousEnd.current;
    previousEnd.current = end;
    startTimeRef.current = null;
    const easingFn = easingFns[opts.easing];

    const animate = (timestamp: number) => {
      startTimeRef.current ??= timestamp;

      const elapsed = timestamp - startTimeRef.current;
      const progress = Math.min(elapsed / opts.duration, 1);
      const easedProgress = easingFn(progress);
      const value = from + (end - from) * easedProgress;

      setCurrent(value);

      if (progress < 1) {
        rafId.current = requestAnimationFrame(animate);
      }
    };

    rafId.current = requestAnimationFrame(animate);

    return () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
    };
  }, [end, opts.duration, opts.start, opts.enabled, opts.easing, reduced]);

  return current;
}

export type { UseCountUpOptions, EasingName as CountUpEasing };
